import { isNil } from 'lodash'

import { ContractOwnerUtil } from '@/utils/ContractOwner'
import { DateTimeUtil } from '@/utils/DateTime'
import { PersonUtil } from '@/utils/Person'
import { LogHelper } from '@/helpers/Log'
import { isRequired } from '@/utils/CustomErrors'

export const StatusUtil = {
  contractOwnerComplete(basket) {
    const owner = basket?.contractOwner
    if (isNil(owner)) return false
    if (isNil(owner.contractOwnerType)) return false
    // TODO: isNil replaced === null
    if (isNil(basket?.email) || isNil(basket?.mobile)) return false

    if (owner.contractOwnerType === 'PERSONID' && isNil(owner.personId)) return false
    if (owner.contractOwnerType === 'CUSTOMER' && (isNil(owner.name) || isNil(owner.customerNr))) return false
    if (
      ContractOwnerUtil.isLegalRepOrNonCustomer(owner) &&
      (isNil(owner.lastName === null) || isNil(owner.firstName) || isNil(owner.gender) || isNil(owner.dateOfBirth))
    )
      return false
    // TODO: Check if zipcode not nil
    // TODO: Backend-Check (Amit)
    if (
      ContractOwnerUtil.isLegalRepOrNonCustomer(owner) &&
      owner.sameAddress === false &&
      (isNil(owner.legalAddress?.street) || isNil(owner.legalAddress?.houseNumber))
    )
      return false

    return true
  },

  /**
   * isAdminDataComplete checks if all data has been entered on the admin data screen
   *
   * @param basket
   * @param isPersonnelNumberRequired
   * @return {boolean}
   */
  isAdminDataComplete(basket, isPersonnelNumberRequired) {
    const { address, collective, customerPortal, existingCustomer, payment } = basket

    const customerPortalComplete =
      existingCustomer ||
      customerPortal?.customerPortalAdvAccepted === false ||
      !!(customerPortal?.customerPortalAdvAccepted && customerPortal?.passwordDeliveryMethod)

    const addressComplete = !!(address?.street && address?.houseNumber)
    const collectiveComplete = StatusUtil.isCollectiveComplete(collective, isPersonnelNumberRequired)
    const contractOwnerComplete = StatusUtil.contractOwnerComplete(basket)
    const paymentComplete = payment?.account ? !!payment?.iban : true

    return addressComplete && contractOwnerComplete && collectiveComplete && customerPortalComplete && paymentComplete
  },

  /**
   * isCollectiveComplete checks if all fields are filled in the collective-box
   *
   * @param collective
   * @param isPersonnelNumberRequired
   * @return {boolean}
   */
  isCollectiveComplete(collective, isPersonnelNumberRequired = false) {
    if (!collective) return true

    if (isPersonnelNumberRequired) return !isNil(collective.personnelNumber)
    return !(!isNil(collective.personnelNumber) && isNil(collective.startDate))
  },

  /**
   * isHealthQuestionsComplete checks if all healthQuestions are answered for all persons
   *
   * @param persons
   * @return {boolean}
   */
  isHealthQuestionsComplete(persons) {
    return persons.every(p => StatusUtil.isHealthQuestionsCompleteForPerson(p.healthQuestionAnswers))
  },

  /**
   * isHealthQuestionsCompleteForPerson checks if a person has answered all available healthQuestions
   *
   * @param answers
   * @return {boolean}
   */
  isHealthQuestionsCompleteForPerson(answers) {
    if (answers === undefined || answers.length === 0) {
      return true
    }

    return answers.every(hq => {
      const isValid = HealthQuestionConfig(hq, answers)[hq.healthQuestionId]
      if (isNil(isValid)) {
        LogHelper.warn(`status-validation missing for ${hq.healthquestionId}`)
        return false
      }
      return isValid
    })
  },

  /**
   * isOnboardingSecured checks if all onboarding data are present
   *
   * @param basket
   * @return {boolean}
   */
  isOnboardingSecured(basket) {
    const { email, mobile } = basket
    return !!email && !!mobile
  },

  /**
   * isPersonDataComplete checks if all the fields on the person-data screen are filled
   *
   * @param basket
   * @param selectedCategoriesMap
   * @return {boolean}
   */
  isPersonDataComplete(basket, selectedCategoriesMap) {
    const persons = PersonUtil.getPersonsToValidate(basket)

    const personalDataComplete = persons.every(person =>
      this.isPersonalDataCompleteForPerson(person.personData, basket.existingCustomer)
    )

    const preInsurerComplete = persons.every(person => {
      return this.isPreInsurerCompleteForPerson(
        person.preInsurer,
        basket.contractStartDate,
        selectedCategoriesMap[person.personId].isVVGOnly
      )
    })

    //FLEX-1027: No work-situation questions for existing customers
    const workSituationComplete = basket.existingCustomer
      ? true
      : persons.every(person => {
          return this.isWorkSituationCompleteForPerson(person.workSituation, {
            isVVGOnly: selectedCategoriesMap[person.personId].isVVGOnly,
            isKVGOnly: selectedCategoriesMap[person.personId].isKVGOnly,
            dateOfBirth: person.personData.dateOfBirth,
            contractStartDate: basket.contractStartDate,
          })
        })

    return personalDataComplete && preInsurerComplete && workSituationComplete
  },

  /**
   * isPersonalDataCompleteForPerson checks if all fields are filled in the
   * personalData-box on the person-data screen for a person
   *
   * @param personData
   * @param existingCustomer
   * @return {boolean}
   */
  isPersonalDataCompleteForPerson(personData = isRequired('personData'), existingCustomer) {
    const hasValidNationalityData = () => {
      /*
        There can be incomplete data (missing nationality or residencePermit) coming from service-portal
        which needs to be handled as valid in DVP
       */
      if (existingCustomer) return true

      return personData.nationality && (personData.nationality === 'CH' || personData.residencePermit)
    }
    return !!(
      personData.dateOfBirth &&
      personData.firstName &&
      personData.lastName &&
      hasValidNationalityData() &&
      personData.gender
    )
  },

  /**
   * isPersonalDataValid checks if all fields on the person screen are valid
   *
   * @param persons
   * @return {boolean}
   */
  isPersonalDataValid(persons) {
    if (persons.length === 0) return false

    const isValidPerson = p => {
      const age = DateTimeUtil.getAge(p?.personData?.dateOfBirth)

      const hasDateOfBirth = p.personData.dateOfBirth
      const hasGender = p.personData.gender
      const hasValidAge =
        (age === -1 && p.personData.gender === 'UNBORN') || (age > -1 && p.personData.gender !== 'UNBORN')
      const hasValidPreInsurerDate = !p.preInsurer?.currentKvgMovedToCH || p.preInsurer?.currentRegistration

      return hasDateOfBirth && hasGender && hasValidAge && hasValidPreInsurerDate
    }
    return persons.every(isValidPerson)
  },

  /**
   * isPreInsurerCompleteForPerson checks if the constellation of preInsurer data is valid for a person
   *
   * @param preInsurer
   * @param contractStartDate
   * @param isVVGOnly
   * @return {boolean}
   */
  isPreInsurerCompleteForPerson(
    preInsurer,
    contractStartDate = isRequired('contractStartDate'),
    isVVGOnly = isRequired('isVVGOnly')
  ) {
    if (!preInsurer || isVVGOnly) return true
    if (isNil(preInsurer.preInsurer)) return false

    const hasPreInsurer = preInsurer.preInsurer === true
    const isFirstOfYear = contractStartDate.slice(5, 10) === '01-01'
    const hasKVGMovedToCH = preInsurer.currentKvgMovedToCH

    // Checks if there is a preInsurer
    if (hasPreInsurer && isFirstOfYear) return !isNil(preInsurer.currentPreInsurerId)
    if (hasPreInsurer) return !isNil(preInsurer.currentPreInsurerId) && !isNil(preInsurer.currentFranchise)

    // Checks for movedKVGToCH
    if (isNil(hasKVGMovedToCH)) return false
    if (!hasKVGMovedToCH) return !isNil(preInsurer.currentKvgOtherReason)

    // There is no preInsurer and user has moved KVG to CH
    return true
  },

  /**
   * isWorkSituationCompleteForPerson checks if all fields in the workSituation-box are filled for a person
   *
   * @param workSituation
   * @param isVVGOnly
   * @param isKVGOnly
   * @param dateOfBirth
   * @param contractStartDate
   * @return {boolean}
   */
  isWorkSituationCompleteForPerson(
    workSituation,
    {
      isVVGOnly = isRequired('isVVGOnly'),
      isKVGOnly = isRequired('isKVGOnly'),
      dateOfBirth = isRequired('dateOfBirth'),
      contractStartDate = isRequired('contractStartDate'),
    }
  ) {
    // Check if a person < 15 ever has a work situation object (as the backend doesn't include it)
    if (isNil(workSituation)) return true
    const ageEndOfContractStartYear = DateTimeUtil.getAgeEndOfContractStartYear(dateOfBirth, contractStartDate)

    const workSituationFields = PersonUtil.getWorkSituationFields(
      ageEndOfContractStartYear,
      { isVVGOnly, isKVGOnly },
      workSituation
    )

    return workSituationFields.filter(field => field.required).every(field => !isNil(workSituation[field.name]))
  },
}

function HealthQuestionConfig(hq, allAnswers) {
  const answeredYes = hq => hq.mainAnswer === 'YES'
  const notAnswered = hq => isNil(hq.mainAnswer)
  const isNotEmpty = arr => arr?.length > 0
  const isPositiveNr = num => num > 0

  const isAnsweredAndValid = (hq, fieldNames) => {
    if (notAnswered(hq)) return false
    if (!answeredYes(hq)) return true
    return fieldNames.some(fieldName => isNotEmpty(hq[fieldName]))
  }

  const hasValidSubAnswer = (hq, fieldName) => {
    if (notAnswered(hq)) return false
    if (!answeredYes(hq)) return true
    if (isNil(hq.subAnswers)) return false

    return !!hq.subAnswers[fieldName]
  }

  const hasDBCasesAnswered = hq => {
    const preconditions = _hq =>
      ['DB_TREATMENTS', 'DB_DEFECTS'].some(condition => {
        return condition === _hq.healthQuestionId && answeredYes(_hq)
      })
    const required = allAnswers.some(_hq => preconditions(_hq))
    if (!required) return true

    const subQuestions = [
      'dentalDenture',
      'dentalMissingTeeth',
      'dentalDeviateTeeth',
      'dentalGumDisease',
      'dentalAccident',
      'dentalAnomaly',
      'dentalRetention',
    ]
    return subQuestions.every(subQuestion => hq?.subAnswers && typeof hq.subAnswers[subQuestion] === 'boolean')
  }

  const hasAnsweredWithValidDate = hq => {
    if (notAnswered(hq)) return false
    if (!answeredYes(hq)) return true

    const { year, month } = hq.subAnswers?.treatmentDate || {}
    if (!year || !month) return false

    const isInFuture = DateTimeUtil.isInFuture(`${year}-${month}-01`)
    return isNotEmpty(hq.doctorIds) && !isInFuture
  }

  return {
    BABY_WEIGHT: isPositiveNr(hq.subAnswers?.birthWeight),
    HEIGHT_WEIGHT: isPositiveNr(hq.subAnswers?.height) && isPositiveNr(hq.subAnswers?.weight),
    LIFESTYLE_MALE: isAnsweredAndValid(hq, ['smokingConsumptions', 'alcoholConsumptions', 'drugConsumptions']),
    LIFESTYLE_FEMALE: isAnsweredAndValid(hq, ['smokingConsumptions', 'alcoholConsumptions', 'drugConsumptions']),
    DISABILITY: isAnsweredAndValid(hq, ['treatmentCases']),
    EMPLOYABILITY: isAnsweredAndValid(hq, ['treatmentCases']),
    TREATMENTS: isAnsweredAndValid(hq, ['treatmentCases']),
    DRUGS: isAnsweredAndValid(hq, ['medicines']),
    DB_DEFECTS: !notAnswered(hq),
    IV: !notAnswered(hq),
    MENSTRUATION: !notAnswered(hq),
    PF_CANCER: !notAnswered(hq),
    PF_FERTILITY: !notAnswered(hq),
    PF_ENDOMETRIOSIS: !notAnswered(hq),
    PF_MENSTRUATION: !notAnswered(hq),
    DB_TREATMENTS: !notAnswered(hq),
    PF_TREATMENTS: !notAnswered(hq),
    PF_PLANNEDTREATMENTS: !notAnswered(hq),
    JAW: !notAnswered(hq),
    JOB: !notAnswered(hq),
    PREGNANCY: hasValidSubAnswer(hq, 'calculatedBirthdate'),
    PF_HEREDITARY_DISEASES: hasValidSubAnswer(hq, 'hereditaryDisease'),
    DB_CASES: hasDBCasesAnswered(hq),
    DB_CONTROL: hasAnsweredWithValidDate(hq),
    DB_CONTROL_Y: hasAnsweredWithValidDate(hq),
    DB_HYGIENE: hasAnsweredWithValidDate(hq),
    DB_FILLINGS: hasValidSubAnswer(hq, 'fillings'),
  }
}
